import hexToRgb from 'pretty-easy-hex-to-rgb';
import { mix, lighten } from 'polished';
import defaults from './tebraDefaults';
import {
  defaultButton,
  primaryButton,
  errorButton,
  linkButton,
} from './tebraButtons';

export const internal = {
  default: defaults.grey500, // synapse 3
  primary: defaults.primaryColor, // growth 1
  secondary: defaults.grey900, // synapse 1
  vitality: defaults.vitalityColor, // vitality 1
  core: defaults.coreColor, // core 1
  error: defaults.errorColor, // first aid
  info: defaults.infoColor, // circadian
  infoRGB: '7, 120, 204', // 4678CC
  success: defaults.successColor, // vigor
  warning: defaults.warningColor, // energize
};

const colors = {
  primary: internal.primary,
  primaryRGB: hexToRgb(internal.primary),
  secondary: internal.secondary,
  secondaryRGB: hexToRgb(internal.secondary),
  textColorPrimary: defaults.textColorPrimary,
  textColorPrimaryRGB: hexToRgb(defaults.textColorPrimary),
  info: internal.info,
  infoRGB: internal.infoRGB,
  light: defaults.grey600,
  lightPrimary: defaults.hoverColor,
  lightBackground: mix(0.9, defaults.white, internal.secondary),
  lightIcon: mix(0.7, defaults.white, internal.secondary),
  dark: defaults.grey900,
  link: internal.primary,
  error: internal.error,
  errorRGB: hexToRgb(internal.error),
  success: internal.success,
  lightText: defaults.textColorSecondary,
  warning: internal.warning,
  warningText: internal.secondary,
  subtleBackground: defaults.white, // synapse 6
  checkbox: defaults.primaryColorLight, // growth 2
  checkboxDisabled: defaults.primaryColor100, // growth 4
  button: {
    borderRadius: '24px',
    fontWeight: 400,
    default: defaultButton,
    primary: primaryButton,
    error: errorButton,
    link: linkButton,
  },
  accents: {
    border: defaults.grey400, // synapse 4
    borderAlt: defaults.grey500, // synapse 3
    shadow: 'rgba(0, 0, 0, 0.1)', // might remove in tebra palette, no shadows
  },
  active: {
    primary: defaults.white,
    secondary: defaults.focusColor, // pneumo
    secondaryAlt: defaults.focusColor, // pneumo
  },
  listView: {
    backgroundColor: mix(0.96, defaults.white, internal.secondary),
    borderColor: defaults.grey400, // synapse 4
    highlightColor: defaults.white,
    selectedColor: defaults.hoverColor,
  },
  fill: {
    default: internal.default,
    primary: internal.primary,
    success: internal.success,
    warning: internal.warning,
    urgent: internal.error,
    error: internal.error,
    secondary: internal.secondary,
    disabled: defaults.grey300,
    excel: internal.warning,
  },
  calendar: {
    appointmentBackground: defaults.white,
    appointmentHighlight: defaults.focusColor, // pneumo
    appointmentBorder: defaults.grey500, // synapse 3
    appointmentShadow: 'rgba(214, 214, 214, 0.25)', // synapse 4
    appointmentText: 'rgba(43, 43, 43, 0.84)', // synapse 1
    appointmentHover: 'rgba(154, 154, 154,0.5)', // synapse 3
    border: 'rgba(214, 214, 214, 0.14)', // synapse 4
    breaksBackgroundColor: defaults.grey400, // synapse 4
    defaultScheduleColor: defaults.vitalityColor, // vitality 1
    headerDayBgColor: defaults.white,
    headerCurrentDayLabelColor: defaults.white,
    headerBgColor: defaults.white,
    highlightDayColor: internal.primary,
    holidayHeaderColor: defaults.focusColor, // pneumo
    holidayBackground: defaults.grey400, // synapse 4,
    hourLine: defaults.vitalityColor, // vitality 1
    hover: defaults.hoverColor, // growth 5
    outSideOfficeHours: defaults.grey300, // synapse 5
    status: {
      scheduled: 'rgb(255, 255, 255)',
      inoffice: lighten(0.44, internal.primary),
      finished: lighten(0.64, internal.secondary),
      cancelled: lighten(0.4, internal.error),
      timeblock: lighten(0.54, internal.secondary),
    },
  },
  footer: {
    background: defaults.backboneColorLight, // backbone 2
    text: defaults.activeColor, // practice
  },
  menuButton: {
    hoverColor: defaults.hoverColor,
  },
  toggleButton: {
    darkTheme: {
      border: internal.core, // core 1
      shadow: 'none',
      gradient1: 'rgb(228, 229, 234)',
      gradient2: 'rgb(217, 218, 222)',
      selectedBackground: internal.primary, // growth 1
      selectedBorder: internal.primary, // growth 1
      selectedShadow: 'none',
      selectedText: defaults.white, // synpase 6
    },
  },
  eRxRequests: {
    border: 'rgba(214, 214, 214, 0.12)', // synapse 4
    categoryBackground: defaults.white,
    categoryBackgroundSelected: 'rgba(214, 214, 214, 0.1)', // synapse 4
    itemHoverBackground: defaults.focusColor, // pnuemo
    itemHighlightAccent: defaults.dataVisInfoColor, // holistic
  },
  progressBarFiller: {
    background: defaults.primaryColor100, // growth 3
    backgroundGradient: '',
    filter: '',
  },
  automatedStatements: {
    switch: defaults.dataVisInfoColor, // holistic
  },
  appointmentCard: {
    headerBgColor: defaults.coreColor200, // core 3
    headerTextColor: defaults.textColorPrimary, // synapse 1
  },
  taskItem: {
    pending: defaults.activeColor, // practice
  },
};

export const colorScale = {
  blue_0: defaults.white,
  blue_5: defaults.hoverColor, // growth 5
  blue_10: defaults.focusColor, // pneumo
  blue_50: defaults.focusColor, // pneumo
  blue_70: defaults.dataVisInfoColor, // holistic
  blue_100: defaults.activeColor, // practice

  grey_0: defaults.white,
  grey_5: defaults.grey300, // synapse 5
  grey_10: defaults.grey400, // synapse 4
  grey_50: defaults.grey500, // synapse 3
  grey_70: defaults.grey600, // synapse 2
  grey_100: defaults.grey900, // synapse 1

  green_0: '#6FC691', // restore
  green_50: defaults.successColor, // vigor
  green_100: defaults.successColorDark, // soma

  orange_0: defaults.warningColorLight, // amino
  orange_50: defaults.warningColor, // energize
  orange_100: defaults.warningColorDark, // neuron

  red_0: defaults.secondaryColorLight, // siren
  red_50: defaults.secondaryColor, // first aid
  red_100: defaults.secondaryColorDark, // remedy
};

export default colors;
